import React, { ComponentType, FC, ReactNode } from 'react';
import { CriticalIcon, HelpIcon, LockIcon } from '../../icons';
import { theme } from '../../theme';

import { RootStack, Label, Hint, Error, BlockTooltip, Disabled } from './styled';
import { useId } from '../../hooks/useId';
import { Stack } from '../../components/Stack';

export interface WithLabelsProps {
  id?: string;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  disabledMessage?: ReactNode;
  tooltip?: string;
  tooltipContent?: ReactNode;
}

export function withLabels<P>(Component: ComponentType<P>): FC<P & WithLabelsProps> {
  return ({ label, hint, error, disabledMessage, tooltip, tooltipContent, id, ...otherProps }) => {
    const componentId = useId({ id, prefix: Component.name });

    if (!label)
      return (
        <Component
          id={componentId}
          hasError={!!error}
          disabled={!!disabledMessage}
          {...(otherProps as P)}
        />
      );

    return (
      <RootStack spacing="sm" alignX="stretch">
        <Stack direction="horizontal" alignY="center" spacing="sm">
          <Label id={`${componentId}-label`} htmlFor={componentId}>
            {label}
          </Label>
          {(tooltip || tooltipContent) && (
            <BlockTooltip text={tooltip} content={tooltipContent}>
              <HelpIcon
                data-testid="tooltip-help"
                width={theme.sizes.base}
                height={theme.sizes.base}
                color={theme.colors.neutral.ink.light}
              />
            </BlockTooltip>
          )}
        </Stack>
        {hint && <Hint id={`${componentId}-hint`}>{hint}</Hint>}
        <Component
          id={componentId}
          hasError={!!error}
          disabled={!!disabledMessage}
          {...(otherProps as P)}
        />
        {error && (
          <Stack id={`${componentId}-error`} direction="horizontal" alignY="center" spacing="xs">
            <CriticalIcon width={16} height={16} color={theme.colors.secondary.red.base} />
            <Error>{error}</Error>
          </Stack>
        )}
        {disabledMessage && (
          <Stack id={`${componentId}-disabled`} direction="horizontal" alignY="center" spacing="xs">
            <LockIcon width={16} height={16} color={theme.colors.neutral.ink.light} />
            <Disabled>{disabledMessage}</Disabled>
          </Stack>
        )}
      </RootStack>
    );
  };
}
